import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import Button from 's2s-button';
import CallHistoryItem from './callHistoryItem';
import { colorGrid } from 's2s-themes';
import { getIntlText } from '../js/utilities';
import styled from 'styled-components';
import useCallHistory from '../hooks/callHistory';
import { combinedSVGIcons } from 's2s-svg-icons-combined';


const CallHistoryContainer = styled.div`
  background-color : ${colorGrid.gray2};
  min-height: calc(100vh - 96px); // minus height of app heading and call history heading
  width : 100%;
`;

const Header = styled.div`
  align-items: center;
  background-color: #182661;
  box-shadow: inset 0 -1px 0 0 rgba(13, 91, 155, 0.1);
  color : ${colorGrid.gray0};
  display: flex;
  flex-direction: row;
  height : 48px;
  justify-content: space-between;
  padding-left : 8px;
  padding-right : 8px;
`;

const BackButton = styled(Button)`
  display : flex;
  justify-content : center;
  padding: 0px;
  width : 32px;

  :hover {
    background-color : ${colorGrid.brand9};
  }
`;

const HeaderTitle = styled.div`
  align-items: center;
  font-size: 20px;
  font-weight: 600;
  display: flex;
  justify-content: center;
  line-height: 24px;
  text-align: center;;
`;

const CallHistoryListContainer = styled.div`
  display : flex;
  flex-direction : column;
  justify-content : ${(props) => {
    // we get an object if there is any call history retrieved
    return typeof (props.callHistory) === 'object' ? "flexStart" : "center";
  }};
  min-height: calc(100vh - 96px); // minus height of app heading and call history heading
  max-height: calc(100vh - 96px); // minus height of app heading and call history heading */
  overflow: auto;
`;

const NoCallHistory = styled.div`
  align-items: center;
  display: flex;
  flex-direction: column;
  padding-top: 135px;
`;

const CallDetailList = styled.div``;

const DateHeading = styled.div`
  padding-left: 16px;
  font-style: normal;
  font-weight: 600;
  font-size: 14px;
  line-height: 22px;
  color: #33414D;
  background-color: #F2F2F3;
`;

const NoHistoryMessage = styled.div`
  align-items : center;
  color: #4C5863;
  display : flex;
  font-size : 14px;
  line-height : 22px;
  text-align : center;
  width : 120px;
`;

const CallHistoryList = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
`;

const CallHistoryListItemContainer = styled.li`
  max-height: calc(100vh - 96px); // minus height of app heading and call history heading
  overflow: auto;
`;

const CallDate = styled.li`
  list-style : none;
`;
const CallHistory = (props) => {
  const { theme } = props;
  const [isRetrieving, setRetrieving] = useState(false);
  const [callHistory, { getCallHistory }] = useCallHistory();
  const loadPage = async () => {
    try {
      setRetrieving(true);
      await getCallHistory();
      setRetrieving(false);
    } catch (error){
      console.error("UNABLE TO LOAD CALL HISTORY: ", error);
    }
  };
  useEffect(() => {
    loadPage();
  }, []);

  const callHistoryClick = (nbr) => {
    //console.log('>>>>>', nbr );
    props.cbOnClick(nbr);
  };

  const closeClick = () => {
    props.cbOnClose();
  };

  /**
    renderCallHistory is a function that takes in an array of objects. 
    It returns an array of objects sorted by lastTimestamp. 
    It is invoked in the constructor.
  **/
  const renderCallHistory = (callHistory = {}) => {
    const callHistoryItemList = Object.keys(callHistory).map((callDate) => {
      const sublist = callHistory[callDate].map((callItem) => {
        let callNumber = callItem.callInfo.direction === "inbound" ? callItem.callInfo.sourceid : callItem.callInfo.destinationid;
        let contactName = callItem.contactName;

        return (
          <CallHistoryListItemContainer
            className="CallHistoryListItemContainer"
            key={callItem.key}
          >
            <CallHistoryItem
              cbClick={callHistoryClick}
              callerNumber={callNumber}
              callerName={contactName}
              direction={callItem.callInfo.direction}
              timestamp={callItem.endTime}
              theme={theme}
            />
          </CallHistoryListItemContainer>
        );
      });

      // return of callHistoryItemList is JSX that renders list including date dividers and sublists.
      return (
        callHistory[callDate].length > 0 ? <CallDate className="CallDate" key={callDate + 'heading'} >
          <CallHistoryList className="CallHistoryList">
            <DateHeading className="DateHeading" key={callDate + 'dateheader'} >
              {callDate}
            </DateHeading>
            {sublist}
          </CallHistoryList>
        </CallDate> : undefined); // end of return
    }); // end of callHistoryItemList

    return callHistoryItemList;
  };

  const noCallHistory = () => {
    return (
      <NoCallHistory className="NoCallHistory" >
        <NoHistoryMessage className="NoHistoryMessage" >
          {getIntlText(props.intl, 'CALLHISTORY-NO-HISTORY-MSG', 'No history items to display')}
        </NoHistoryMessage>
      </NoCallHistory>
    );
  };

  const retrievingHistory = () => {
    return (
      <NoCallHistory className="NoCallHistory" >
        <NoHistoryMessage className="NoHistoryMessage" >
          {getIntlText(props.intl, 'CALLHISTORY-RETRIEVING-HISTORY-MSG', 'Busy Retrieving call history.')}
        </NoHistoryMessage>
      </NoCallHistory>
    );
  };

  const callHistoryList = (isRetrieving === true ? retrievingHistory() : (callHistory.length === 0 ?
    noCallHistory() :
    (<CallDetailList className='callDetailList' >
      {renderCallHistory(callHistory)}
    </CallDetailList>)));
  return (
    <CallHistoryContainer className="CallHistoryContainer">
      <Header className="Header" >
        <BackButton
          ariaLabel="Back button"
          className="backBtn"
          buttonSize="medium"
          buttonType="ghost"
          svgFill={colorGrid.gray0}
          svgType={'MdKeyboardArrowLeft'}
          cbClick={closeClick}
          tabIndex="0"
        />
        <HeaderTitle className="HeaderTitle" >
          {getIntlText(props.intl, 'CALLHISTORY-HEADER', 'History')}
        </HeaderTitle>
        <div className="dummyItem" style={{ width: '32px' }} />
      </Header>
      <CallHistoryListContainer
        className="callHistoryListContainer"
        callHistory={callHistory}
      >
        {callHistoryList}
      </CallHistoryListContainer>
    </CallHistoryContainer>
  );
};

CallHistory.propTypes = {
  callDetails: PropTypes.array,
  cbClearHistory: PropTypes.func,
  cbOnClick: PropTypes.func,
  cbOnClose: PropTypes.func,
  intl: PropTypes.object
};

CallHistory.defaultProps = {
  callDetails: [],
  cbOnClose: () => { console.log("cbOnClose not defined "); },
  cbClearHistory: () => { console.log("cbClearHistory not defined "); }
};

export default CallHistory;
